import { useEffect, useRef, useState } from "react";

/** Pause after the last keystroke before the issues list is queried. */
export const ISSUE_SEARCH_DEBOUNCE_MS = 300;

/**
 * A search that names one issue: its number (`42`), identifier (`BUG-42`), or id.
 * Title search stays inside the current list; a lookup should still find a
 * sub-issue, a done issue, or an archived one.
 */
export function isIssueLookup(q: string): boolean {
  const value = q.trim();
  if (!value) return false;
  if (/^#?\d+$/.test(value)) return true;
  if (/^[A-Za-z][A-Za-z0-9]{0,9}-\d+$/.test(value)) return true;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
}

/**
 * The text in the box, updated immediately, and committed to the URL after
 * `delay` ms. Clearing the box commits immediately. A URL change this hook did
 * not write (back, or Clear filters) replaces the draft.
 */
export function useDraftQuery(
  urlValue: string,
  commit: (value: string) => void,
  delay = ISSUE_SEARCH_DEBOUNCE_MS,
) {
  const [value, setValue] = useState(urlValue);
  const [trackedUrl, setTrackedUrl] = useState(urlValue);
  const commitRef = useRef(commit);
  commitRef.current = commit;
  const writtenRef = useRef(urlValue);

  if (urlValue !== trackedUrl) {
    setTrackedUrl(urlValue);
    if (urlValue !== writtenRef.current && value !== urlValue) setValue(urlValue);
  }

  useEffect(() => {
    if (value === urlValue) {
      writtenRef.current = urlValue;
      return;
    }
    const delayMs = value === "" ? 0 : delay;
    const id = setTimeout(() => {
      writtenRef.current = value;
      commitRef.current(value);
    }, delayMs);
    return () => clearTimeout(id);
  }, [value, urlValue, delay]);

  return [value, setValue] as const;
}
